import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { curso } from '@curso/content/curso';
import { COLETA, gravarContato, type ResultadoDoEnvio } from '@inscricao/services/contatos';

/**
 * O texto do consentimento, versionado.
 *
 * A versao nao e enfeite: a prova do consentimento exige saber QUAL texto a
 * pessoa aceitou. Cada contato gravado leva a versao junto, na coluna
 * `consentimento_versao`. Sem isso, daqui a um ano ninguem consegue dizer com
 * o que cada pessoa concordou.
 *
 * Mudou o texto, suba a versao. Nao reaproveite numero.
 *
 * 2026-10-20.1: a finalidade passou a cobrir "esta e as proximas turmas", para
 * a guarda ate a pessoa pedir para sair ser coerente com o que ela aceitou; o
 * institucional passou a incluir cargo e organizacao.
 */
export const CONSENTIMENTO = {
  versao: '2026-10-20.1',
  lista:
    'Autorizo o uso do meu nome e contato para receber avisos sobre as inscrições desta e das próximas turmas da Imersão HCRM, por e-mail e WhatsApp. Posso pedir para sair a qualquer momento.',
  institucional:
    'Autorizo o uso do meu nome, contato, cargo e organização para a coordenação da Imersão HCRM falar comigo sobre a formação da minha equipe, nesta e nas próximas turmas, por e-mail e WhatsApp. Posso pedir para sair a qualquer momento.',
} as const;

export type Finalidade = 'lista' | 'institucional';

/**
 * De onde veio o clique: qual botao da pagina abriu o dialogo.
 *
 * Vai para a coluna `origem` do banco. Nao e dado pessoal; serve para saber
 * qual bloco traz mais cadastros.
 */
export type Origem =
  | 'cabecalho'
  | 'abertura'
  | 'como-funciona'
  | 'o-que-leva'
  | 'fim-de-semana'
  | 'in-company'
  | 'chamada-final'
  | 'rodape';

/**
 * E-mail com domínio de verdade.
 *
 * O `type="email"` do navegador aceita `ana@hospital`, sem ponto nenhum depois
 * da arroba, porque é endereço válido numa rede interna. Para um aviso que vai
 * sair pela internet, isso é erro de digitação, não endereço.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/;

/**
 * Telefone brasileiro: DDD e número, sem o 55. O preenchimento automático
 * pede o formato nacional (`tel-national`) justamente para o 55 não entrar.
 *
 * DDD não tem zero em nenhum dos dois dígitos. Número de nove dígitos começa
 * com 9; o de oito fica aceito porque WhatsApp Business roda em fixo.
 */
export function telefoneValido(valor: string) {
  const digitos = valor.replace(/\D/g, '');
  return /^[1-9]{2}(9\d{8}|[2-8]\d{7})$/.test(digitos);
}

/** Vai formatando enquanto a pessoa digita: (11) 98765-4321. */
function mascararTelefone(valor: string) {
  const d = valor.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  const corte = resto.length > 8 ? 5 : 4;
  if (resto.length <= corte) return `(${ddd}) ${resto}`;
  return `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`;
}

/** Mensagem do próprio balão de erro do navegador, em português e dizendo o formato. */
function exigir(campo: HTMLInputElement, valido: boolean, mensagem: string) {
  campo.setCustomValidity(valido || campo.value === '' ? '' : mensagem);
}

type Abrir = (finalidade: Finalidade, origem: Origem) => void;

const ContatoContexto = createContext<Abrir>(() => {});

/** Abre o diálogo de contato de qualquer lugar da página. */
export const useContato = () => useContext(ContatoContexto);

/**
 * O diálogo de contato.
 *
 * Um formulário só, para a página inteira. Usa o `<dialog>` nativo de
 * propósito: ele traz de graça o que um modal caseiro erra, como foco preso
 * dentro, Esc fechando e o resto da página inerte para leitor de tela.
 *
 * Grava de verdade só com a coleta ligada (VITE_COLETA=true). Desligada, o
 * formulário abre, valida e diz com todas as letras que nada foi guardado.
 * Ligar depende de política de privacidade e encarregado indicado, que são
 * do grupo, o controlador dos dados.
 */
export function ProvedorDeContato({ children }: { children: ReactNode }) {
  const [aberto, setAberto] = useState<{ finalidade: Finalidade; origem: Origem } | null>(null);
  const dialogo = useRef<HTMLDialogElement>(null);

  const abrir = useCallback<Abrir>((finalidade, origem) => setAberto({ finalidade, origem }), []);

  useEffect(() => {
    const elemento = dialogo.current;
    if (!elemento) return;
    if (aberto && !elemento.open) elemento.showModal();
    if (!aberto && elemento.open) elemento.close();
  }, [aberto]);

  return (
    <ContatoContexto.Provider value={abrir}>
      {children}
      <dialog
        ref={dialogo}
        onClose={() => setAberto(null)}
        /* Clique na camada escura, fora do painel, fecha. */
        onClick={(evento) => {
          if (evento.target === dialogo.current) setAberto(null);
        }}
        /*
          `m-auto` e o que centraliza. O <dialog> nativo se centra sozinho por
          margin:auto, e o reset do Tailwind zera a margem de tudo, entao sem
          esta classe ele encosta no canto superior esquerdo.
        */
        className="m-auto w-[min(30rem,calc(100vw-2rem))] border border-linha-forte bg-papel p-0 text-tinta backdrop:bg-tinta/50 backdrop:backdrop-blur-sm"
      >
        {aberto ? (
          <Formulario
            finalidade={aberto.finalidade}
            origem={aberto.origem}
            aoFechar={() => setAberto(null)}
          />
        ) : null}
      </dialog>
    </ContatoContexto.Provider>
  );
}

type Situacao = 'preenchendo' | 'enviando' | ResultadoDoEnvio | 'rascunho';

function Formulario({
  finalidade,
  origem,
  aoFechar,
}: {
  finalidade: Finalidade;
  origem: Origem;
  aoFechar: () => void;
}) {
  const base = useId();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [cargo, setCargo] = useState('');
  const [organizacao, setOrganizacao] = useState('');
  const [aceito, setAceito] = useState(false);
  const [situacao, setSituacao] = useState<Situacao>('preenchendo');

  const institucional = finalidade === 'institucional';

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!COLETA) {
      setSituacao('rascunho');
      return;
    }
    setSituacao('enviando');
    const digitos = whatsapp.replace(/\D/g, '');
    const resultado = await gravarContato({
      nome: nome.trim().replace(/\s+/g, ' '),
      email: email.trim().toLowerCase(),
      ddd: digitos.slice(0, 2),
      telefone: digitos.slice(2),
      finalidade,
      cargo: institucional ? cargo.trim() : null,
      organizacao: institucional ? organizacao.trim() : null,
      consentimento_versao: CONSENTIMENTO.versao,
      origem,
    });
    setSituacao(resultado);
  }

  const campo =
    'w-full border-b border-tinta-fraca bg-transparent px-1 py-2.5 text-apoio text-tinta transition-colors placeholder:text-tinta-fraca focus:border-tinta focus:outline-none';

  if (situacao !== 'preenchendo' && situacao !== 'enviando' && situacao !== 'erro') {
    const primeiroNome = nome.trim().split(' ')[0];
    const titulo = {
      rascunho: 'Este é um rascunho',
      gravado: `Pronto, ${primeiroNome}`,
      repetido: 'Você já está na lista',
    }[situacao];
    const texto = {
      rascunho:
        'Nada foi guardado. Ninguém vai receber mensagem. O site ainda não está registrando contatos.',
      gravado: institucional
        ? 'A coordenação procura você para conversar sobre a formação da sua equipe.'
        : 'Você recebe o aviso assim que as inscrições abrirem.',
      repetido: institucional
        ? 'Este e-mail já está cadastrado para inscrições institucionais. A coordenação procura você.'
        : 'Este e-mail já está cadastrado. Você recebe o aviso assim que as inscrições abrirem.',
    }[situacao];

    return (
      <div className="p-7" role="status">
        <p className="display-item">{titulo}</p>
        <p className="mt-3 text-apoio leading-relaxed text-tinta-suave">{texto}</p>
        <button
          type="button"
          onClick={aoFechar}
          className="mt-7 min-h-11 bg-tinta px-6 text-miudo font-medium text-papel transition-colors hover:bg-destaque"
        >
          Fechar
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-5 p-7">
      <div>
        <h2 className="display-item">
          {institucional
            ? 'Leve a experiência HCRM para a sua equipe'
            : 'Seja avisado quando as inscrições abrirem!'}
        </h2>
        {institucional ? (
          <p className="mt-2 text-apoio leading-relaxed text-tinta-suave">
            Deixe seu contato para conversarmos sobre as possibilidades e condições especiais
            para inscrições institucionais.
          </p>
        ) : (
          <>
            <p className="mt-2 text-apoio leading-relaxed text-tinta-suave">
              A {curso.formato} terá uma turma exclusiva de {curso.turma}, para proporcionar uma
              experiência de aprendizagem próxima, prática e participativa.
            </p>
            <p className="mt-2 text-apoio leading-relaxed text-tinta-suave">
              Cadastre seu interesse e receba em primeira mão, por e-mail ou WhatsApp, as
              informações sobre a abertura das inscrições, que serão realizadas pelo{' '}
              {curso.parceiro}.
            </p>
          </>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <label htmlFor={`${base}-nome`} className="sr-only">
            Seu nome completo
          </label>
          <input
            id={`${base}-nome`}
            type="text"
            required
            minLength={3}
            maxLength={200}
            autoComplete="name"
            autoFocus
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            placeholder="Nome completo"
            className={campo}
          />
        </div>

        {institucional ? (
          <>
            <div>
              <label htmlFor={`${base}-cargo`} className="sr-only">
                Seu cargo
              </label>
              <input
                id={`${base}-cargo`}
                type="text"
                required
                minLength={2}
                maxLength={150}
                autoComplete="organization-title"
                value={cargo}
                onChange={(evento) => setCargo(evento.target.value)}
                placeholder="Cargo"
                className={campo}
              />
            </div>
            <div>
              <label htmlFor={`${base}-organizacao`} className="sr-only">
                Sua organização
              </label>
              <input
                id={`${base}-organizacao`}
                type="text"
                required
                minLength={2}
                maxLength={200}
                autoComplete="organization"
                value={organizacao}
                onChange={(evento) => setOrganizacao(evento.target.value)}
                placeholder="Organização"
                className={campo}
              />
            </div>
          </>
        ) : null}

        <div>
          <label htmlFor={`${base}-email`} className="sr-only">
            Seu e-mail
          </label>
          <input
            id={`${base}-email`}
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            value={email}
            onChange={(evento) => {
              setEmail(evento.target.value);
              exigir(
                evento.target,
                EMAIL.test(evento.target.value.trim()),
                'Confira o e-mail. Ele precisa ter o formato nome@dominio.com.',
              );
            }}
            placeholder="seu@email.com"
            className={campo}
          />
        </div>

        <div>
          <label htmlFor={`${base}-whatsapp`} className="sr-only">
            Seu WhatsApp
          </label>
          <input
            id={`${base}-whatsapp`}
            type="tel"
            required
            inputMode="tel"
            autoComplete="tel-national"
            value={whatsapp}
            onChange={(evento) => {
              const formatado = mascararTelefone(evento.target.value);
              setWhatsapp(formatado);
              exigir(
                evento.target,
                telefoneValido(formatado),
                'Informe o DDD e o número do celular, por exemplo (11) 98765-4321.',
              );
            }}
            placeholder="WhatsApp com DDD: (11) 98765-4321"
            className={campo}
          />
        </div>
      </div>

      {/*
        Nao vem marcada, e nunca pode vir. Consentimento presumido, do tipo
        "ao enviar voce concorda", nao e consentimento.
      */}
      <label
        htmlFor={`${base}-aceite`}
        className="flex cursor-pointer items-start gap-2.5 text-miudo leading-relaxed text-tinta-suave"
      >
        <input
          id={`${base}-aceite`}
          type="checkbox"
          required
          checked={aceito}
          onChange={(evento) => setAceito(evento.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-current"
        />
        <span>
          {institucional ? CONSENTIMENTO.institucional : CONSENTIMENTO.lista}{' '}
          <a
            href="/privacidade"
            target="_blank"
            rel="noopener"
            className="text-destaque underline underline-offset-2"
          >
            Aviso de privacidade
          </a>
        </span>
      </label>

      {situacao === 'erro' ? (
        <p role="alert" className="text-miudo leading-relaxed text-tinta">
          Não deu para registrar agora. Confira sua conexão e tente de novo em alguns minutos.
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={situacao === 'enviando'}
          className="min-h-11 bg-tinta px-6 text-miudo font-medium text-papel transition-colors hover:bg-destaque disabled:opacity-60"
        >
          {situacao === 'enviando'
            ? 'Enviando…'
            : institucional
              ? 'Quero as condições'
              : 'Quero ser avisado'}
        </button>
        <button
          type="button"
          onClick={aoFechar}
          className="min-h-11 px-2 text-miudo text-tinta-fraca underline underline-offset-2"
        >
          Fechar
        </button>
      </div>
    </form>
  );
}

/**
 * O botão que abre o diálogo.
 *
 * Existe para que nenhum bloco precise saber que há um diálogo: ele pede
 * "contato" e o provedor resolve. A origem diz de qual bloco veio o clique.
 */
export function BotaoDeContato({
  finalidade = 'lista',
  origem,
  invertido = false,
  children,
}: {
  finalidade?: Finalidade;
  origem: Origem;
  invertido?: boolean;
  children?: ReactNode;
}) {
  const abrir = useContato();

  return (
    <button
      type="button"
      onClick={() => abrir(finalidade, origem)}
      className={`min-h-11 px-6 text-miudo font-medium transition-colors ${
        invertido
          ? 'bg-papel text-tinta hover:bg-papel/85'
          : 'bg-tinta text-papel hover:bg-destaque'
      }`}
    >
      {children ?? (finalidade === 'institucional' ? 'Quero as condições' : 'Quero ser avisado')}
    </button>
  );
}
